import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { formatToString } from 'utils/formatMoney'
import styles from 'modules/Finance/Wallets/subcomponents/Chart/Doughnut.module.scss'

ChartJS.register(ArcElement, Tooltip, Legend)

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div')

  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.className = styles['doughnut__tooltip']
    tooltipEl.style.position = 'absolute'

    const table = document.createElement('table')
    table.className = styles['doughnut__tooltip-table']

    tooltipEl.appendChild(table)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }

  return tooltipEl
}

const externalTooltipHandler = (context, currency) => {
  // Tooltip Element
  const { chart, tooltip } = context
  const tooltipEl = getOrCreateTooltip(chart)

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  // Set Text
  if (tooltip.body) {
    const bodyLines = tooltip.body.map((b) => b.lines)

    const tableBody = document.createElement('tbody')
    bodyLines.forEach((body) => {
      const tr = document.createElement('tr')

      const td = document.createElement('td')
      td.className = styles['doughnut__tooltip-body']

      const splitBody = body[0].split(':')

      const text = document.createTextNode(
        splitBody[0] +
          ' ' +
          formatToString(splitBody[1].replace(/\s/g, ''), currency),
      )

      const div = document.createElement('div')
      div.className = styles['doughnut__tooltip-text']
      div.appendChild(text)

      td.appendChild(div)
      tr.appendChild(td)
      tableBody.appendChild(tr)
    })

    const tableRoot = tooltipEl.querySelector('table')

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove()
    }

    // Add new children
    tableRoot.appendChild(tableBody)
  }

  const {
    offsetLeft: positionX,
    offsetTop: positionY,
    offsetWidth,
  } = chart.canvas

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1
  tooltipEl.style.left = positionX + offsetWidth / 2 + 'px'
  tooltipEl.style.top = positionY - 50 + 'px'
}

export const DoughnutChart = ({ labels, colors, data, currency }) => {
  const isZeroData = useMemo(() => data.length === 1 && data[0] === 0, [data])

  const stateData = useMemo(
    () => ({
      labels: labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderWidth: 0,
          hoverBorderWidth: 0,
          borderAlign: 'inner',
        },
      ],
    }),
    [data, labels, colors],
  )

  const options = useMemo(
    () => ({
      responsive: true,
      cutoutPercentage: 100,

      cutout: '83%',

      plugins: {
        legend: false,
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
          position: 'nearest',
          external: (context) => externalTooltipHandler(context, currency),
        },
      },
    }),
    [],
  )

  return (
    <div
      className={clsx(styles['doughnut'], {
        [styles['doughnut_zero-data']]: isZeroData,
      })}
    >
      <Doughnut type="doughnut" data={stateData} options={options} />
    </div>
  )
}

DoughnutChart.propTypes = {
  labels: PropTypes.array,
  colors: PropTypes.array,
  data: PropTypes.array,
  currency: PropTypes.string,
}
